/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import DatePicker, { registerLocale } from 'react-datepicker';
import { getInitialProps, useTranslation } from 'react-i18next';
import moment from 'moment';
import styles from './RangeDatePicker.module.scss';
import 'react-datepicker/dist/react-datepicker.css';
import Icon from '@mui/material/Icon';
import { Typography } from '@mui/material';
import { theme } from '../../assets/style/them';
import ru from 'date-fns/locale/ru';
import az from 'date-fns/locale/az';
import { useEffect, useRef } from 'react';
registerLocale('ru', ru);
registerLocale('az', az);
const pickerFormat: string = 'YYYY-MM-DD HH:mm';
const shortPickerFormat: string = 'YYYY-MM-DD';

interface RangeDatePickerOwnProps {
    dateTo?: string;
    dateFrom?: string;
    dateFormat?: string;
    isShowTime?: boolean;
    isMinDate?: boolean;
    isMaxDate?: boolean;
    minDateSubstrAmount?: number;
    timeInterval?: number;
    minTime?: Date;
    maxTime?: Date;
    minDate?: Date;
    onDateChange: ({ dateFrom, dateTo }: { dateFrom: moment.Moment; dateTo: moment.Moment }) => void;
    column?: boolean;
    wrap?: boolean;
    isCustomLabel?: boolean;
    isIcon?: boolean;
    popperPlacement?: string;
    inputWidth?: number;
    disabled?: boolean;
}

const RangeDatePicker = (props: RangeDatePickerOwnProps) => {
    const {
        onDateChange,
        dateTo,
        dateFrom,
        dateFormat,
        isShowTime,
        isMinDate,
        isMaxDate,
        timeInterval,
        minDateSubstrAmount,
        column,
        wrap,
        minTime,
        maxTime,
        minDate,
        isCustomLabel,
        isIcon,
        popperPlacement,
        inputWidth,
        disabled,
    } = props;

    const { t } = useTranslation();
    const languagePickerDate = getInitialProps().initialLanguage;
    const datePickerRef = useRef(null);

    useEffect(() => {
        if (datePickerRef.current && inputWidth) {
            const inputs = datePickerRef.current.getElementsByTagName('input');

            for (const item of inputs) {
                item.style.width = `${inputWidth}px`;
            }
        }
    }, [datePickerRef.current]);

    const handleChangeStartDate = (date: Date) => {
        const startDate = isShowTime ? moment(date) : moment(date).startOf('day');
        let endDate = moment(dateTo);

        if (startDate.isAfter(endDate)) endDate = startDate.clone().endOf('day');

        onDateChange({ dateFrom: startDate, dateTo: endDate });
    };

    const handleChangeEndDate = (date: Date) => {
        let startDate = moment(dateFrom);
        const endDate = isShowTime ? moment(date) : moment(date).endOf('day');

        if (endDate.isBefore(startDate)) startDate = endDate.clone().startOf('day');

        onDateChange({ dateFrom: startDate, dateTo: endDate });
    };

    return (
        <div
            ref={datePickerRef}
            style={{ flexDirection: !column ? 'row' : 'column', flexWrap: wrap ? 'wrap' : 'nowrap' }}
            className={styles.form_control_wrapper}
        >
            <div className={styles.from_container}>
                {!isCustomLabel ? (
                    <label htmlFor="dateSelect">
                        <Typography noWrap variant="subtitle1">
                            {`${t('app.date')}`}
                        </Typography>
                    </label>
                ) : null}
                <div className={styles.input_icon_wrap} style={{ marginBottom: wrap ? theme.spacing(1) : 0 }}>
                    {isIcon ? <Icon className={styles.icon}> {'date_range_icon'} </Icon> : null}
                    <DatePicker
                        locale={languagePickerDate}
                        onChange={(date) => handleChangeStartDate(date)}
                        placeholderText={`${t('app.from')}:`}
                        selected={new Date(moment(dateFrom).format(isShowTime ? pickerFormat : shortPickerFormat))}
                        startDate={new Date(moment(dateFrom).format(isShowTime ? pickerFormat : shortPickerFormat))}
                        endDate={new Date(moment(dateTo).format(isShowTime ? pickerFormat : shortPickerFormat))}
                        value={moment(dateFrom).format(isShowTime ? pickerFormat : shortPickerFormat)}
                        selectsStart
                        showTimeSelect={isShowTime}
                        timeFormat="HH:mm"
                        timeIntervals={timeInterval ? timeInterval : 1}
                        dateFormat={dateFormat ? dateFormat : 'yyyy-MM-dd h:mm aa'}
                        timeCaption={`${t('app.time')}:`}
                        maxDate={isMaxDate === false ? null : new Date()}
                        minDate={
                            isMinDate
                                ? !minDate
                                    ? new Date(moment(dateTo).clone().subtract(minDateSubstrAmount, 'day').format())
                                    : minDate
                                : null
                        }
                        showDisabledMonthNavigation
                        minTime={minTime ? minTime : null}
                        maxTime={maxTime ? maxTime : null}
                        popperProps={{
                            positionFixed: true,
                        }}
                        popperPlacement={popperPlacement ? popperPlacement : 'bottom-start'}
                        disabled={disabled}
                    />
                </div>
            </div>

            <div
                style={{ marginTop: column ? theme.spacing(2) : 0, marginBottom: wrap ? theme.spacing(1) : 0 }}
                className={styles.to_container}
            >
                <div className={styles.input_icon_wrap}>
                    {isIcon ? <Icon className={styles.icon}> {'date_range_icon'} </Icon> : null}
                    <DatePicker
                        locale={languagePickerDate}
                        onChange={(date) => handleChangeEndDate(date)}
                        placeholderText={`${t('app.to')}:`}
                        selected={new Date(moment(dateTo).format(isShowTime ? pickerFormat : shortPickerFormat))}
                        startDate={new Date(moment(dateFrom).format(isShowTime ? pickerFormat : shortPickerFormat))}
                        endDate={new Date(moment(dateTo).format(isShowTime ? pickerFormat : shortPickerFormat))}
                        value={moment(dateTo).format(isShowTime ? pickerFormat : shortPickerFormat)}
                        selectsEnd
                        showTimeSelect={isShowTime}
                        timeFormat="HH:mm"
                        timeIntervals={timeInterval ? timeInterval : 1}
                        dateFormat={dateFormat ? dateFormat : 'yyyy-MM-dd h:mm aa'}
                        timeCaption={`${t('app.time')}:`}
                        maxDate={isMaxDate === false ? null : new Date()}
                        minDate={
                            isMinDate
                                ? !minDate
                                    ? new Date(moment(dateTo).clone().subtract(minDateSubstrAmount, 'day').format())
                                    : minDate
                                : null
                        }
                        showDisabledMonthNavigation
                        minTime={minTime ? minTime : null}
                        maxTime={maxTime ? maxTime : null}
                        popperProps={{
                            positionFixed: true,
                        }}
                        popperPlacement={popperPlacement ? popperPlacement : 'bottom-start'}
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );
};

export default RangeDatePicker;
